import _ from "lodash";

import { BASE_URL } from "config/urls";
import { get, post, useFetch, usePaginatedFetch } from "utils/sdk";

import { IUploadedFile } from "entities/File/sdk";

export interface IAMPusherResponse {
  success: boolean;
  detail: string;
  error?: string;
}

interface IDateInfo {
  year: Date;
  month: number;
  quarter: number | null;
}
interface IProcessAssetsData {
  file_id: number;
  date_info: Partial<IDateInfo>;
}
export interface IAMDateInfo {
  year: number | string;
  quarter?: number | null;
  month?: number | null;
}

export type IAMDateValueInfo =
  | { id: null; value: number; aggregated: true }
  | { id: number; value: number; aggregated: false };

export interface IAMDateValue {
  Audited?: IAMDateValueInfo;
  Budgeted?: IAMDateValueInfo;
  Actual?: IAMDateValueInfo;
  date_info: IAMDateInfo;
}

export interface IAMDataPoint {
  name: string;
  code: string;
  is_deleted?: boolean;
  date_values: Array<IAMDateValue>;
}

export interface IAMSubCategory {
  key: string;
  data_points: Array<IAMDataPoint>;
  total_values: Array<IAMDateValue>;
}

export interface IAssetManagementData {
  [key: string]: Array<IAMSubCategory>;
}

export interface IAssetManagementHeadersData {
  year: number | string;
  quarter?: number | null;
}

export interface IAssetManagementFetchData {
  data: IAssetManagementData;
  headers: Array<IAssetManagementHeadersData>;
}

export interface IAssetManagementFile {
  id: number;
  original_file_name: string;
  created_at: string;
}

interface IAssetManagementFileDetail {
  id: number;
  url: string;
  original_file_name: string;
  created_at: string;
}

export const processAssetsData = (dealId: number, data: IProcessAssetsData) => {
  const formatData = {
    file_id: data.file_id,
    date_info: {
      year: data.date_info.year?.getFullYear(),
      month: _.isEmpty(data.date_info.month) ? null : data.date_info.month,
      quarter: _.isEmpty(data.date_info.quarter)
        ? null
        : data.date_info.quarter,
    },
  };
  post(
    `${BASE_URL}v2/asset-management/deal/${dealId}/process-assets-data/`,
    formatData
  );
};

export interface IAssetManagementDataV2 {
  headers: Array<{
    year: number | string;
    quarter: number | null;
    month: number | null;
  }>;
  categories: Array<IAssetManagementCategory>;
}

export interface IAssetManagementCategory {
  id: number;
  name: string;
  sub_categories: Array<IAssetManagementSubCategory>;
}

export interface IAssetManagementSubCategory {
  id: number;
  name: string;
  info_text: string;
  is_calculation_item: boolean;
  rows: Array<IAssetManagementRow>;
  data: { name: string; values: Array<IAssetManagementDataValue> };
}

export interface IAssetManagementRow {
  id: number | null;
  name: string;
  item_code: string;
  values: Array<IAssetManagementRowValue>;
}

export interface IAssetManagementDataValue {
  value: number | null;
  data_type: string;
  date_info: IAMDateInfo;
  is_division_by_zero?: boolean;
}

export interface IAssetManagementRowValue extends IAssetManagementDataValue {
  id: number | null;
}

/**
 * Interface for bridging the old AM Table structure with the new AM data fetching approach.
 *
 * Usage:
 * - `allDataRows` variables are created by iterating through each category and sub-category.
 * - Each row is augmented with the `sub_category` key, so the current Table structure knows its original location.
 * - Additional fields such as `is_calculation_item`, `is_total_row` and `sub_category_info_text` are added to support the
 *   calculation items feature.
 *
 */
export interface IAssetManagementRowOldStructure extends IAssetManagementRow {
  sub_category: {
    id: number;
    name: string;
    category: { id: number; name: string };
  };
  is_calculation_item: boolean;
  is_total_row: boolean;
  sub_category_info_text: string;
}

export interface IAssetManagementRows {
  rows: Array<IAssetManagementRowOldStructure>;
}

export const useAssetManagementData = ({ dealId }: { dealId: number }) =>
  useFetch<IAssetManagementDataV2>(
    `v2/asset-management/deal/${dealId}/fetch-assets-data/`
  );

export const useAssetManagementFileList = ({
  dealId,
  limit = 10,
  enabled = true,
}: {
  dealId: number;
  limit?: number;
  enabled?: boolean;
}) =>
  usePaginatedFetch<IUploadedFile>({
    url: enabled ? `v2/asset-management/deal/${dealId}/files/list/` : undefined,
    params: { limit },
    options: {
      dedupingInterval: 0,
    },
  });

export const getAssetManagementFile = (
  dealId: number,
  fileId: number
): Promise<IAssetManagementFileDetail> =>
  get(`${BASE_URL}v2/asset-management/deal/${dealId}/file/${fileId}`);

export interface IDiff {
  rows_diff: {
    [key: string]: {
      is_deleted: boolean;
      order: number | null;
      previous_label: string | null;
      current_label: string | null;
    };
  };
  cells_diff: {
    [key: string]: {
      previous_value: number | null;
      current_value: number | null;
    };
  };
}

export const useVersionsDiff = ({
  prevVersionId,
  newVersionId,
}: {
  prevVersionId: number | undefined;
  newVersionId: number | undefined;
}) =>
  useFetch<IDiff>(
    !_.isUndefined(prevVersionId) && !_.isUndefined(newVersionId)
      ? `v2/asset-management/am-versions/${prevVersionId}/${newVersionId}/diff/`
      : undefined
  );

interface IVersion {
  id: number;
  version_number: number;
  created_by: {
    first_name: string;
    last_name: string;
  };
  created_at: string;
}

export const useVersions = ({ dealId }: { dealId: number }) =>
  useFetch<Array<IVersion>>(
    `v2/asset-management/deal/${dealId}/am-versions/list/`
  );

export const updateAssetManagement = ({
  dealId,
  diff,
}: {
  dealId: number;
  diff: Array<{
    key: string;
    label: string;
    values: Array<{
      key: string;
      value: number | null; //string;
    }>;
  }>;
}) =>
  post(`${BASE_URL}v2/asset-management/deal/${dealId}/proforma-update/`, diff);

export const useAssetManagementCategories = ({ dealId }: { dealId: number }) =>
  useFetch<Array<IAssetManagementCategory>>(
    `v2/asset-management/deal/${dealId}/category/list/`
  );
